import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import * as TemplateActions from './templates.actions';
import { Template } from 'src/app/models/template';
import { Language } from 'src/app/models/language';
import { LANGUAGES } from 'src/app/core/dashboard-constants';
import { transformLanguageCodesToLanguageEntities } from 'src/app/core/languages-helper';

export interface TemplateState extends EntityState<Template> {
  loadingTemplates: boolean;
  templatesLoaded: boolean;
  loadingTemplate: boolean;
  templateLoaded: boolean;
  selectedTemplate: Template;
  supportedLanguages: Language[];
  selectedLanguage: Language;
  importingTemplate: boolean;
  templateImported: boolean;
}

export const templateAdapter: EntityAdapter<Template> = createEntityAdapter<Template>({});

export const initialTemplateState: TemplateState = templateAdapter.getInitialState({
  loadingTemplates: true,
  templatesLoaded: false,
  loadingTemplate: false,
  templateLoaded: false,
  selectedTemplate: null,
  supportedLanguages: [LANGUAGES[0]],
  selectedLanguage: LANGUAGES[0],
  importingTemplate: false,
  templateImported: false,
});

export const reducer = createReducer(
  initialTemplateState,
  on(TemplateActions.getTemplates, (state) => {
    return {
      ...state,
      loadingTemplates: true,
      templatesLoaded: false,
      selectedTemplate: null,
    };
  }),
  on(TemplateActions.getTemplatesSuccess, (state, { templates }) => {
    return templateAdapter.setAll(templates, {
      ...state,
      loadingTemplates: false,
      templatesLoaded: true,
      supportedLanguages: [LANGUAGES[0]],
      selectedLanguage: LANGUAGES[0],
    });
  }),
  on(TemplateActions.getTemplatesError, (state) => {
    return {
      ...state,
      loadingTemplates: false,
      templatesLoaded: false,
    };
  }),
  on(TemplateActions.setSelectedTemplate, (state, { selectedTemplate }) => {
    const transformedLanguages = transformLanguageCodesToLanguageEntities(selectedTemplate.languages);

    return {
      ...state,
      selectedTemplate,
      supportedLanguages: transformedLanguages,
      selectedLanguage: transformedLanguages[0],
    };
  }),
  on(TemplateActions.getTemplate, (state) => {
    return {
      ...state,
      loadingTemplate: true,
      templateLoaded: false,
    };
  }),
  on(TemplateActions.getTemplateSuccess, (state, { selectedTemplate }) => {
    return templateAdapter.updateOne(
      { id: selectedTemplate.id, changes: selectedTemplate },
      {
        ...state,
        loadingTemplate: false,
        templateLoaded: true,
        selectedTemplate,
      }
    );
  }),
  on(TemplateActions.getTemplateError, (state) => {
    return {
      ...state,
      loadingTemplate: false,
      templateLoaded: false,
    };
  }),
  on(TemplateActions.addTemplateSupportedLanguages, (state, { supportedLanguages }) => {
    return {
      ...state,
      supportedLanguages,
    };
  }),
  on(TemplateActions.setSelectedLanguage, (state, { selectedLanguage }) => {
    return {
      ...state,
      selectedLanguage,
    };
  }),
  on(TemplateActions.createTemplateSuccess, (state, { createdTemplate }) => {
    return templateAdapter.addOne(createdTemplate, {
      ...state,
      selectedTemplate: createdTemplate,
    });
  }),
  on(TemplateActions.updateTemplateSuccess, (state, { updatedTemplate }) => {
    return templateAdapter.updateOne(
      { id: updatedTemplate?.id, changes: updatedTemplate },
      {
        ...state,
        selectedTemplate: updatedTemplate,
      }
    );
  }),
  on(TemplateActions.deleteTemplateSuccess, (state, { deletedTemplate }) => {
    return templateAdapter.removeOne(deletedTemplate.id, {
      ...state,
      selectedTemplate: null,
      supportedLanguages: [LANGUAGES[0]],
      selectedLanguage: LANGUAGES[0],
    });
  }),
  on(TemplateActions.importTemplate, (state) => {
    return {
      ...state,
      importingTemplate: true,
      templateImported: false,
    };
  }),
  on(TemplateActions.importTemplateSuccess, (state, { importedTemplate }) => {
    return templateAdapter.addOne(importedTemplate, {
      ...state,
      importingTemplate: false,
      templateImported: true,
    });
  }),
  on(TemplateActions.importTemplateError, (state) => {
    return {
      ...state,
      importingTemplate: false,
      templateImported: false,
    };
  })
);

export function TemplateReducer(state: TemplateState | undefined, action: Action) {
  return reducer(state, action);
}

export const { selectIds, selectEntities, selectAll, selectTotal } = templateAdapter.getSelectors();
